import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

export type DividerOrientation = 'horizontal' | 'vertical';

/**
 * A visual separator for breaking content into distinct sections.
 *
 * Renders a 1px horizontal or vertical rule. Any content placed in the default
 * slot appears centered on the rule, splitting it into two equal flanking lines.
 * Without slot content, a single continuous rule spans the full container width
 * (horizontal) or height (vertical).
 *
 * ## Usage
 *
 * ```html
 * <!-- Plain rule -->
 * <asc-divider></asc-divider>
 *
 * <!-- Heading -->
 * <asc-divider><h2>Notifications</h2></asc-divider>
 *
 * <!-- Text -->
 * <asc-divider>Today</asc-divider>
 *
 * <!-- Single button -->
 * <asc-divider><asc-button variant="secondary">Add</asc-button></asc-divider>
 *
 * <!-- Multiple buttons (visually grouped by the consumer) -->
 * <asc-divider>
 *   <asc-button variant="secondary">View all</asc-button>
 *   <asc-button variant="secondary">Active</asc-button>
 * </asc-divider>
 *
 * <!-- Vertical rule -->
 * <asc-divider orientation="vertical" style="height: 120px;"></asc-divider>
 * ```
 *
 * ## Accessibility
 *
 * - Renders `role="separator"` with `aria-orientation` matching the `orientation` prop.
 * - For purely decorative dividers, add `aria-hidden="true"` to the element.
 * - Slotted content lives in light DOM and is fully readable by screen readers.
 *
 * @element asc-divider
 *
 * @slot - Optional content centered on the rule. Accepts any HTML: plain
 *   text, headings, `<asc-button>` elements, `<asc-button-group>`, etc. When
 *   empty, a single unbroken rule is rendered.
 *
 * @csspart base       - The inner structural container (carries `role="separator"`).
 * @csspart line       - Style hook for all rule line segments.
 * @csspart line-start - The leading rule segment (present only when slot has content).
 * @csspart line-end   - The trailing rule segment (present only when slot has content).
 *
 * @cssprop --asc-divider-line-color        - Line border color. Default: `var(--asc-border-secondary)`
 * @cssprop --asc-divider-line-thickness    - Line border width (`border-bottom` for horizontal, `border-right` for vertical). Default: `1px`
 * @cssprop --asc-divider-gap              - Gap between rule and slotted content (horizontal). Default: `var(--asc-spacing-md)`
 * @cssprop --asc-divider-gap-vertical     - Gap between rule and slotted content (vertical). Default: `var(--asc-spacing-xl)`
 */
@customElement('asc-divider')
export class AscDivider extends LitElement {
  static override styles = css`
    :host {
      display: block;
      width: 100%;
      box-sizing: border-box;
    }

    :host([orientation='vertical']) {
      width: auto;
      height: 100%;
    }

    .base {
      display: flex;
      align-items: center;
      gap: 0;
      width: 100%;
      height: 100%;
    }

    .base.has-content {
      gap: var(--asc-divider-gap, var(--asc-spacing-md));
    }

    :host([orientation='vertical']) .base {
      flex-direction: column;
      align-items: center;
      width: auto;
    }

    :host([orientation='vertical']) .base.has-content {
      gap: var(--asc-divider-gap-vertical, var(--asc-spacing-xl));
    }

    /* ── Rule line segments ──────────────────────────────────── */
    .line {
      flex: 1 0 0;
      border-bottom: var(--asc-divider-line-thickness, 1px) solid var(--asc-divider-line-color, var(--asc-border-secondary));
      height: 0;
    }

    :host([orientation='vertical']) .line {
      border-bottom: none;
      border-right: var(--asc-divider-line-thickness, 1px) solid var(--asc-divider-line-color, var(--asc-border-secondary));
      height: auto;
      width: 0;
    }

    /* Content slot — flex-shrink keeps it from being squeezed by the lines */
    .content-slot {
      flex-shrink: 0;
    }

    .content-slot--empty {
      display: none;
    }
  `;

  /** Controls the layout axis of the divider rule. */
  @property({ type: String, reflect: true }) orientation: DividerOrientation = 'horizontal';

  @state() private _hasContent = false;

  private _handleSlotChange(e: Event): void {
    const slot = e.target as HTMLSlotElement;
    this._hasContent = slot.assignedNodes({ flatten: true }).length > 0;
  }

  override render() {
    return html`
      <div
        part="base"
        role="separator"
        aria-orientation=${this.orientation}
        class=${this._hasContent ? 'base has-content' : 'base'}
      >
        ${this._hasContent
          ? html`<div part="line line-start" class="line"></div>`
          : html`<div part="line" class="line"></div>`}
        <slot
          class=${this._hasContent ? 'content-slot' : 'content-slot content-slot--empty'}
          @slotchange=${this._handleSlotChange}
        ></slot>
        ${this._hasContent ? html`<div part="line line-end" class="line"></div>` : ''}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'asc-divider': AscDivider;
  }
}
